import { type CSSProperties } from 'react';
import { AppearanceConfig, CornerRadiusMode, TimerType } from '@/types';

export const MIN_CORNER_RADIUS = 0;
export const MAX_CORNER_RADIUS = 60;
export const DEFAULT_CORNER_RADIUS = 12;

export type CornerRadiusValues = {
	topLeft: number;
	topRight: number;
	bottomRight: number;
	bottomLeft: number;
};

export type CornerRadiusInputParseResult = {
	inputValue: string;
	modelValue: number;
};

export type CornerRadiusCssVariables = CSSProperties &
	Record<
		| '--timer-corner-radius-top-left'
		| '--timer-corner-radius-top-right'
		| '--timer-corner-radius-bottom-right'
		| '--timer-corner-radius-bottom-left',
		string
	>;

export type DefaultCornerRadiusConfig = Pick<
	AppearanceConfig,
	| 'timer_corner_radius_mode'
	| 'timer_corner_radius_all'
	| 'timer_corner_radius_top_left'
	| 'timer_corner_radius_top_right'
	| 'timer_corner_radius_bottom_left'
	| 'timer_corner_radius_bottom_right'
>;

export function buildDefaultCornerRadius(timerType: TimerType): DefaultCornerRadiusConfig {
	const radius = timerType === TimerType.PRODUCT_CONTAINER ? DEFAULT_CORNER_RADIUS : MIN_CORNER_RADIUS;

	return {
		timer_corner_radius_mode: CornerRadiusMode.ALL,
		timer_corner_radius_all: radius,
		timer_corner_radius_top_left: radius,
		timer_corner_radius_top_right: radius,
		timer_corner_radius_bottom_left: radius,
		timer_corner_radius_bottom_right: radius,
	};
}

export function normalizeCornerRadiusValue(value: unknown): number {
	if (typeof value !== 'number' || !Number.isInteger(value) || value < MIN_CORNER_RADIUS) {
		return MIN_CORNER_RADIUS;
	}

	return Math.min(MAX_CORNER_RADIUS, value);
}

export function normalizeCornerRadiusMode(value: unknown): CornerRadiusMode {
	return value === CornerRadiusMode.SEPARATE ? CornerRadiusMode.SEPARATE : CornerRadiusMode.ALL;
}

export function parseCornerRadiusInput(rawValue: string): CornerRadiusInputParseResult {
	if (/^\s*-/.test(rawValue)) {
		return { inputValue: '0', modelValue: MIN_CORNER_RADIUS };
	}

	const digits = rawValue.replace(/\D/g, '');

	if (digits === '') {
		return { inputValue: '', modelValue: MIN_CORNER_RADIUS };
	}

	const modelValue = normalizeCornerRadiusValue(Number.parseInt(digits, 10));

	return {
		inputValue: String(modelValue),
		modelValue,
	};
}

export function getCornerRadiusValues(
	appearance: Pick<
		AppearanceConfig,
		| 'timer_corner_radius_mode'
		| 'timer_corner_radius_all'
		| 'timer_corner_radius_top_left'
		| 'timer_corner_radius_top_right'
		| 'timer_corner_radius_bottom_left'
		| 'timer_corner_radius_bottom_right'
	>,
): CornerRadiusValues {
	if (appearance.timer_corner_radius_mode === CornerRadiusMode.SEPARATE) {
		return {
			topLeft: normalizeCornerRadiusValue(appearance.timer_corner_radius_top_left),
			topRight: normalizeCornerRadiusValue(appearance.timer_corner_radius_top_right),
			bottomRight: normalizeCornerRadiusValue(appearance.timer_corner_radius_bottom_right),
			bottomLeft: normalizeCornerRadiusValue(appearance.timer_corner_radius_bottom_left),
		};
	}

	const radius = normalizeCornerRadiusValue(appearance.timer_corner_radius_all);

	return {
		topLeft: radius,
		topRight: radius,
		bottomRight: radius,
		bottomLeft: radius,
	};
}

export function buildCornerRadiusVariables(appearance: AppearanceConfig): CornerRadiusCssVariables {
	const values = getCornerRadiusValues(appearance);

	return {
		'--timer-corner-radius-top-left': `${values.topLeft}px`,
		'--timer-corner-radius-top-right': `${values.topRight}px`,
		'--timer-corner-radius-bottom-right': `${values.bottomRight}px`,
		'--timer-corner-radius-bottom-left': `${values.bottomLeft}px`,
	};
}
